/**
 * SharePointBrowser - the SharePoint tab of the Files picker (F-DMS).
 *
 * Browses with the connection made in Settings, inside the modal: the
 * user's own OneDrive and the sites they follow, any site by search, a
 * site's document libraries, then folders. Files are ticked like Files
 * rows; the modal imports them into Files on "Use selected".
 *
 * Replaces Microsoft's OneDrive.js popup, which signed the user in a second
 * time and lost the pick on its way back (`REACT_APP_SHAREPOINT_POPUP_PICKER`
 * brings that back).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  PiBooksLight,
  PiBuildingsLight,
  PiCaretRightLight,
  PiCloudLight,
  PiFolderOpenLight,
  PiLockSimpleLight,
  PiMagnifyingGlassLight,
  PiPlugsLight,
  PiWarningCircleLight,
} from 'react-icons/pi';
import Button from '../TailwindComponents/Button';
import EmptyState from '../TailwindComponents/EmptyState';
import SearchInput from '../TailwindComponents/SearchInput';
import Skeleton from '../TailwindComponents/Skeleton';
import { FolderGlyph } from '../../Files/fileGlyphs';
import PickerFileRow from './PickerFileRow';
import { IntegrationNotConnectedError } from './externalImport';
import { INTEGRATIONS_SETTINGS_PATH, isAccepted } from './filesPickerTypes';
import {
  fetchSharePointHome,
  fetchSharePointItems,
  fetchSharePointLibraries,
  isImportable,
  searchSharePointSites,
  SharePointBrowseError,
  SharePointHome,
  SharePointItem,
  SharePointLibrary,
  SharePointSite,
} from './sharePointBrowse';

/** Where the browser is: a site (its libraries) or a folder in a drive (root when no itemId). */
type Crumb =
  | { kind: 'site'; id: string; name: string }
  | { kind: 'folder'; driveId: string; itemId?: string; name: string };

type View =
  | { kind: 'home'; home: SharePointHome }
  | { kind: 'sites'; sites: SharePointSite[] }
  | { kind: 'libraries'; libraries: SharePointLibrary[] }
  | { kind: 'items'; items: SharePointItem[]; nextPage: string | null };

export const sharePointKey = (item: { id: string; drive_id?: string }) => `${item.drive_id || ''}:${item.id}`;

export interface SharePointBrowserProps {
  accept?: string[];
  isSelected: (key: string) => boolean;
  /** False once `maxFiles` is reached - unticked rows are then disabled. */
  canSelectMore: boolean;
  onToggle: (item: SharePointItem) => void;
  onNotConnected: () => void;
}

const MIN_SITE_QUERY = 2;

const NavRow: React.FC<{ icon: React.ReactNode; label: string; aria: string; onClick: () => void }> = ({
  icon,
  label,
  aria,
  onClick,
}) => (
  <li>
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-start text-sm text-fg-body transition-colors hover:bg-surface-sunken/70"
      aria-label={aria}
    >
      <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center text-fg-muted [&>svg]:h-5 [&>svg]:w-5">{icon}</span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <PiCaretRightLight className="h-4 w-4 flex-shrink-0 text-fg-muted" aria-hidden />
    </button>
  </li>
);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li className="px-2.5 pb-1 pt-3 text-xs font-medium text-fg-muted first:pt-0">{children}</li>
);

const SharePointBrowser: React.FC<SharePointBrowserProps> = ({
  accept,
  isSelected,
  canSelectMore,
  onToggle,
  onNotConnected,
}) => {
  const { t, i18n } = useTranslation();
  const [path, setPath] = useState<Crumb[]>([]);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [view, setView] = useState<View | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    const handle = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(handle);
  }, [search]);

  const here = path.length ? path[path.length - 1] : null;
  // Search covers the whole library, so it is named after it ("Search in Documents").
  const lastSite = path.map((c) => c.kind).lastIndexOf('site');
  const libraryName = path.slice(lastSite + 1).find((c) => c.kind === 'folder')?.name;

  const requestId = useRef(0);
  // A ref, so a parent passing a fresh callback each render never reloads the list.
  const notConnected = useRef(onNotConnected);
  notConnected.current = onNotConnected;
  const fail = useCallback((err: unknown) => {
    if (err instanceof IntegrationNotConnectedError) {
      notConnected.current();
      return;
    }
    setError(err instanceof SharePointBrowseError ? err.code : 'UNKNOWN');
  }, []);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setView(null);
    setError(null);
    try {
      let next: View;
      if (!here) {
        next =
          query.length >= MIN_SITE_QUERY
            ? { kind: 'sites', sites: await searchSharePointSites(query) }
            : { kind: 'home', home: await fetchSharePointHome() };
      } else if (here.kind === 'site') {
        const libraries = await fetchSharePointLibraries(here.id);
        if (id !== requestId.current) return;
        if (libraries.length === 1) {
          // One library is the usual case: go straight in, under the site's name.
          setPath((prev) => [...prev.slice(0, -1), { kind: 'folder', driveId: libraries[0].id, name: here.name }]);
          return;
        }
        next = { kind: 'libraries', libraries };
      } else {
        const page = await fetchSharePointItems(here.driveId, { itemId: here.itemId, q: query || undefined });
        next = { kind: 'items', items: page.items, nextPage: page.next_page };
      }
      if (id === requestId.current) setView(next);
    } catch (err) {
      if (id === requestId.current) fail(err);
    }
  }, [here, query, fail]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMore = async () => {
    if (view?.kind !== 'items' || !view.nextPage || here?.kind !== 'folder') return;
    const id = requestId.current;
    setLoadingMore(true);
    try {
      const page = await fetchSharePointItems(here.driveId, { itemId: here.itemId, q: query || undefined, page: view.nextPage });
      if (id !== requestId.current) return;
      setView({ kind: 'items', items: [...view.items, ...page.items], nextPage: page.next_page });
    } catch (err) {
      if (id === requestId.current) fail(err);
    } finally {
      setLoadingMore(false);
    }
  };

  const go = (next: Crumb[]) => {
    setSearch('');
    setQuery('');
    setPath(next);
  };
  const open = (crumb: Crumb) => go([...path, crumb]);

  const formatDate = (iso?: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(i18n?.language || undefined, { dateStyle: 'medium' });
  };

  const siteRows = (sites: SharePointSite[]) =>
    sites.map((site) => (
      <NavRow
        key={site.id}
        icon={<PiBuildingsLight />}
        label={site.name}
        aria={t('Open site {{name}}', { name: site.name })}
        onClick={() => open({ kind: 'site', id: site.id, name: site.name })}
      />
    ));

  const renderBody = () => {
    if (error) {
      if (error === 'RECONNECT_REQUIRED') {
        return (
          <EmptyState
            density="compact"
            icon={<PiPlugsLight />}
            headline={t('Reconnect SharePoint')}
            subtext={t('Microsoft no longer accepts the saved connection. Reconnect it in Settings, then come back here.')}
            button={{ label: t('Open Settings'), onClick: () => window.open(INTEGRATIONS_SETTINGS_PATH, '_blank', 'noopener') }}
          />
        );
      }
      if (error === 'ACCESS_DENIED' || error === 'NOT_FOUND') {
        return (
          <EmptyState
            density="compact"
            icon={<PiLockSimpleLight />}
            headline={error === 'ACCESS_DENIED' ? t("You don't have access to this location") : t('This location no longer exists')}
            button={{ label: t('Back'), onClick: () => go(path.slice(0, -1)) }}
          />
        );
      }
      return (
        <EmptyState
          density="compact"
          icon={<PiWarningCircleLight />}
          headline={t('Could not load SharePoint')}
          button={{ label: t('Try again'), onClick: load }}
        />
      );
    }
    if (!view) return <Skeleton variant="list" rows={6} />;

    if (view.kind === 'home') {
      const { drive, sites, sites_available } = view.home;
      return (
        <ul className="space-y-1">
          {drive && (
            <>
              <SectionLabel>{t('My files')}</SectionLabel>
              <NavRow
                icon={<PiCloudLight />}
                label={drive.name || 'OneDrive'}
                aria={t('Open {{name}}', { name: drive.name || 'OneDrive' })}
                onClick={() => open({ kind: 'folder', driveId: drive.id, name: drive.name || 'OneDrive' })}
              />
            </>
          )}
          <SectionLabel>{t('Sites you follow')}</SectionLabel>
          {sites.length ? (
            siteRows(sites)
          ) : (
            <li className="px-2.5 py-2 text-sm text-fg-muted">
              {sites_available ? (
                t('Search for a site above, or follow one in SharePoint to keep it here.')
              ) : (
                <>
                  {t('To see your SharePoint sites here, reconnect SharePoint in Settings.')}{' '}
                  <a
                    href={INTEGRATIONS_SETTINGS_PATH}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-fg-strong underline decoration-champagne underline-offset-4 hover:text-champagne"
                  >
                    {t('Open Settings')}
                  </a>
                </>
              )}
            </li>
          )}
        </ul>
      );
    }

    if (view.kind === 'sites') {
      return view.sites.length ? (
        <ul className="space-y-1">{siteRows(view.sites)}</ul>
      ) : (
        <EmptyState density="compact" icon={<PiMagnifyingGlassLight />} headline={t('No sites match your search')} />
      );
    }

    if (view.kind === 'libraries') {
      return view.libraries.length ? (
        <ul className="space-y-1">
          {view.libraries.map((lib) => (
            <NavRow
              key={lib.id}
              icon={<PiBooksLight />}
              label={lib.name}
              aria={t('Open {{name}}', { name: lib.name })}
              onClick={() => open({ kind: 'folder', driveId: lib.id, name: lib.name })}
            />
          ))}
        </ul>
      ) : (
        <EmptyState density="compact" icon={<PiFolderOpenLight />} headline={t('This site has no document libraries')} />
      );
    }

    const sorted = [...view.items].sort(
      (a, b) => Number(b.is_folder) - Number(a.is_folder) || a.name.localeCompare(b.name),
    );
    if (!sorted.length) {
      return (
        <EmptyState
          density="compact"
          icon={<PiFolderOpenLight />}
          headline={query ? t('No files match your search') : t('This folder is empty')}
        />
      );
    }
    return (
      <>
        <ul className="space-y-1">
          {sorted.map((item) =>
            item.is_folder ? (
              <li key={`folder-${item.id}`}>
                <button
                  type="button"
                  onClick={() => open({ kind: 'folder', driveId: item.drive_id, itemId: item.id, name: item.name })}
                  className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-start text-sm text-fg-body transition-colors hover:bg-surface-sunken/70"
                  aria-label={t('Open folder {{name}}', { name: item.name })}
                >
                  <FolderGlyph folderName={item.name} className="h-5 w-5 flex-shrink-0" />
                  <span className="min-w-0 flex-1 truncate">{item.name}</span>
                  <PiCaretRightLight className="h-4 w-4 flex-shrink-0 text-fg-muted" aria-hidden />
                </button>
              </li>
            ) : (
              (() => {
                const key = sharePointKey(item);
                const checked = isSelected(key);
                const unsupported = !isAccepted(item.name, accept) || !isImportable(item.name);
                return (
                  <PickerFileRow
                    key={`file-${key}`}
                    name={item.name}
                    size={item.size}
                    mime={item.mime_type}
                    detail={formatDate(item.modified)}
                    checked={checked}
                    disabled={unsupported || (!checked && !canSelectMore)}
                    unsupported={unsupported}
                    onToggle={() => onToggle(item)}
                  />
                );
              })()
            ),
          )}
        </ul>
        {view.nextPage && (
          <div className="flex justify-center py-2">
            <Button variant="outline" size="small" isLoading={loadingMore} onClick={loadMore}>
              {t('Load more')}
            </Button>
          </div>
        )}
      </>
    );
  };

  const showSearch = !here || here.kind === 'folder';

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3" data-testid="sharepoint-browser">
      <nav aria-label={t('Folder path')} className="flex flex-wrap items-center gap-1 text-sm">
        <button
          type="button"
          onClick={() => go([])}
          className={clsx('rounded px-1 transition-colors hover:text-fg-strong', path.length ? 'text-fg-muted' : 'text-fg-strong')}
          aria-current={path.length ? undefined : 'page'}
        >
          {t('SharePoint')}
        </button>
        {path.map((crumb, index) => (
          <React.Fragment key={`${index}-${crumb.kind === 'site' ? crumb.id : `${crumb.driveId}:${crumb.itemId || ''}`}`}>
            <PiCaretRightLight className="h-3 w-3 text-fg-muted" aria-hidden />
            <button
              type="button"
              onClick={() => go(path.slice(0, index + 1))}
              className={clsx(
                'max-w-[12rem] truncate rounded px-1 transition-colors hover:text-fg-strong',
                index === path.length - 1 ? 'text-fg-strong' : 'text-fg-muted',
              )}
              aria-current={index === path.length - 1 ? 'page' : undefined}
            >
              {crumb.name}
            </button>
          </React.Fragment>
        ))}
      </nav>

      {showSearch && (
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder={here ? t('Search in {{name}}', { name: libraryName || here.name }) : t('Search sites')}
          aria-label={here ? t('Search in {{name}}', { name: libraryName || here.name }) : t('Search sites')}
          size="small"
        />
      )}

      <div className="min-h-[16rem] flex-1 overflow-y-auto custom-scrollbar-main" data-testid="sharepoint-list">
        {renderBody()}
      </div>
    </div>
  );
};

export default SharePointBrowser;
